import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable consistent-return */


import React, { useCallback, useMemo, useRef, useReducer } from 'react';
import { Portal } from "../Portal";
import styled from 'styled-components';
import { ToastItem } from "./ToastItem";
import { ToastContext } from "./ToastContext";
import { AnimatePresence } from 'framer-motion';
import { theme } from "../../theme";
/**
 * Toast state handler
 */

var placementOptions = ['top', 'bottom', 'top-left', 'top-right', 'bottom-left', 'bottom-right'];

var defaultState = _objectSpread({
  placements: {}
}, placementOptions.reduce(function (accumulator, option) {
  // eslint-disable-next-line no-param-reassign
  accumulator[option] = [];
  return accumulator;
}, {}));

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  switch (action.type) {
    case 'HEIGHT':
      {
        var placement = state.placements[action.payload.id];
        var toasts = state[placement];
        return _objectSpread(_objectSpread({}, state), {}, _defineProperty({}, placement, toasts.map(function (toast) {
          if (toast.id === action.payload.id) {
            return _objectSpread(_objectSpread({}, toast), {}, {
              height: action.payload.height
            });
          }

          return toast;
        })));
      }

    case 'ADD':
      {
        var _placement = action.payload.toast.placement || 'top';

        var _toasts = state[_placement];
        return _objectSpread(_objectSpread({}, state), {}, _defineProperty({
          placements: _objectSpread(_objectSpread({}, state.placements), {}, _defineProperty({}, action.payload.toast.id, _placement))
        }, _placement, [].concat(_toConsumableArray(_toasts), [action.payload.toast])));
      }

    case 'REMOVE':
      {
        var _placement2 = state.placements[action.payload.id];
        var _toasts2 = state[_placement2];
        return _objectSpread(_objectSpread({}, state), {}, _defineProperty({}, _placement2, _toasts2.filter(function (toast) {
          return toast.id !== action.payload.id;
        })));
      }

    default:
      return state;
  }
}
/**
 * Provide a place in the DOM to launch our toasts. This should typically
 * be at the uppermost part of your app tree.
 */


export function ToastProvider(_ref) {
  var children = _ref.children;
  var idCounterRef = useRef(0);

  var _useReducer = useReducer(reducer, defaultState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      placements = _useReducer2[0],
      dispatch = _useReducer2[1];

  var addToast = useCallback(function (message, options) {
    var id = idCounterRef.current;
    idCounterRef.current += 1;

    var newToast = _objectSpread({
      id: id,
      message: message
    }, options);

    dispatch({
      type: 'ADD',
      payload: {
        toast: newToast
      }
    });
    return id;
  }, []);
  var removeToast = useCallback(function (id) {
    dispatch({
      type: 'REMOVE',
      payload: {
        id: id
      }
    });
  }, []);
  var toastContext = useMemo(function () {
    return {
      addToast: addToast,
      removeToast: removeToast
    };
  }, [addToast, removeToast]);
  return /*#__PURE__*/React.createElement(ToastContext.Provider, {
    value: toastContext
  }, /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(React.Fragment, null, placementOptions.map(function (placement) {
    var toastsInPlace = placements[placement];
    var precedingHeight = 0;
    return /*#__PURE__*/React.createElement(ToastContainer, {
      key: placement,
      "data-placement": placement
    }, /*#__PURE__*/React.createElement(AnimatePresence, null, toastsInPlace.map(function (toast) {
      var _toast$height;

      var toastHeight = (_toast$height = toast.height) !== null && _toast$height !== void 0 ? _toast$height : 0;
      precedingHeight += toastHeight;

      function registerHeight(height) {
        dispatch({
          type: 'HEIGHT',
          payload: {
            id: toast.id,
            height: height
          }
        });
      }

      return /*#__PURE__*/React.createElement(ToastItem, {
        precedingHeight: precedingHeight - toastHeight,
        registerHeight: registerHeight,
        removeToast: removeToast,
        key: toast.id,
        toast: toast,
        placement: placement
      });
    })));
  }))), children);
}
var ToastContainer = styled.div.withConfig({
  displayName: "ToastProvider__ToastContainer",
  componentId: "vapor__sc-6wkq91-0"
})(["pointer-events:none;position:fixed;left:0;right:0;display:flex;align-items:center;contain:layout;z-index:", ";&[data-placement^='bottom']{bottom:0;flex-direction:column-reverse;}&[data-placement^='top']{top:0;flex-direction:column;}&[data-placement*='left']{align-items:flex-start;}&[data-placement*='right']{align-items:flex-end;}"], theme.zIndices.toast);